/**
 * Offhoo behavior analytics (page_view / button_click / modal_view).
 * page_view: runs when this script loads (MPA), and again on App tab visible.
 * modal_view: overlay / dialog exposure (e.g. spin wheel); separate from page_view PV.
 */
(function (global) {
    const root = global.OffhooAnalytics || (global.OffhooAnalytics = {});

    const VISITOR_KEY = 'offhoo_visitor_id';
    const UTM_KEY = 'offhoo_utm';
    const UTM_TTL_MS = 24 * 60 * 60 * 1000;
    const TRACK_PATH = '/api/v1/shopfiy/offhoo/track';

    const APP_ONLY_PAGES = { category: true, me: true };
    const TRACKED_PAGES = {
        homepage: true,
        category: true,
        cart: true,
        me: true,
        productlist: true,
        productdetail: true,
        searchresult: true,
    };

    /** App bottom Tab → page_title */
    const TAB_TO_PAGE = {
        home: 'homepage',
        category: 'category',
        cart: 'cart',
        mine: 'me',
    };

    /** App bottom Tab → button_name for tab exposure */
    const TAB_TO_BUTTON_NAME = {
        home: 'app_home',
        category: 'app_category',
        cart: 'app_cart',
        mine: 'app_me',
    };

    /** category / cart / me → button_name (first App tab entry without from_app_tab) */
    const PAGE_TO_APP_TAB_BUTTON = {
        category: 'app_category',
        cart: 'app_cart',
        me: 'app_me',
    };

    const FROM_APP_TAB_PARAM = 'from_app_tab';

    /** Boot already counted as app_nav_tab; skip one late onTabVisible for same load. */
    let appTabBootCounted = false;

    /**
     * @returns {string}
     */
    function getPageTitle() {
        const cfg = global.OffhooAppConfig || {};
        const name = String(cfg.templateName || '');
        const suffix = String(cfg.templateSuffix || '');

        if (name === 'index') return 'homepage';
        if (name === 'list-collections') return 'category';
        if (name === 'cart') return 'cart';
        if (name === 'page' && suffix === 'user-center') return 'me';
        if (name === 'collection') return 'productlist';
        if (name === 'product') return 'productdetail';
        if (name === 'search') return 'searchresult';
        return '';
    }

    /**
     * @returns {boolean}
     */
    function isInApp() {
        try {
            return Boolean(global.OffhooApp && typeof global.OffhooApp.isInApp === 'function' && global.OffhooApp.isInApp());
        } catch (_e) {
            return false;
        }
    }

    /**
     * @returns {'ios'|'android'|'h5'|'pc'}
     */
    function getPlatform() {
        try {
            if (global.OffhooApp && typeof global.OffhooApp.getClientChannel === 'function') {
                const channel = global.OffhooApp.getClientChannel();
                if (channel === 'ios' || channel === 'android' || channel === 'h5' || channel === 'pc') {
                    return channel;
                }
            }
        } catch (_e) {
            /* ignore */
        }

        try {
            if (global.OffhooApp && typeof global.OffhooApp.getAppPlatform === 'function') {
                const p = global.OffhooApp.getAppPlatform();
                if (p === 'IOS') return 'ios';
                if (p === 'ANDROID') return 'android';
            }
        } catch (_e) {
            /* ignore */
        }

        const ua = String(global.navigator?.userAgent || '');
        if (/Android|iPhone|iPad|iPod|Mobile|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua)) {
            return 'h5';
        }
        return 'pc';
    }

    /**
     * @param {'ios'|'android'|'h5'|'pc'} platform
     * @returns {'phone'|'desktop'}
     */
    function getDevice(platform) {
        if (platform === 'pc') return 'desktop';
        return 'phone';
    }

    /**
     * @returns {string}
     */
    function getCookie(name) {
        try {
            const match = global.document.cookie.match(
                new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)')
            );
            return match ? decodeURIComponent(match[1]) : '';
        } catch (_e) {
            return '';
        }
    }

    /**
     * session_id: use _shopify_y as agreed; empty if missing.
     * @returns {string}
     */
    function getSessionId() {
        return getCookie('_shopify_y') || '';
    }

    /**
     * @returns {string}
     */
    function createId() {
        try {
            if (global.crypto && typeof global.crypto.randomUUID === 'function') {
                return global.crypto.randomUUID();
            }
        } catch (_e) {
            /* ignore */
        }
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }

    /**
     * Persistent visitor / device id.
     * @returns {string}
     */
    function getVisitorId() {
        try {
            let id = global.localStorage.getItem(VISITOR_KEY) || '';
            if (!id) {
                id = createId();
                global.localStorage.setItem(VISITOR_KEY, id);
            }
            return id;
        } catch (_e) {
            return createId();
        }
    }

    /**
     * @returns {string}
     */
    function getCustomerId() {
        const fromTheme = global.Theme?.offhoo?.customerId;
        if (fromTheme != null && fromTheme !== '') return String(fromTheme);
        return '';
    }

    /**
     * @returns {string}
     */
    function getLocale() {
        return (
            String((global.Shopify && global.Shopify.locale) || '') ||
            String(global.document?.documentElement?.lang || '') ||
            ''
        );
    }

    /**
     * Read UTM / click ids from URL; if present, overwrite localStorage (1 day TTL).
     * Always return current stored values (or empty).
     * @returns {Record<string, string>}
     */
    function syncAndGetUtm() {
        const empty = {
            utm_source: '',
            utm_medium: '',
            utm_campaign: '',
            utm_term: '',
            utm_content: '',
            fbclid: '',
        };

        let fromUrl = null;
        try {
            const params = new URLSearchParams(global.location.search || '');
            const utm_source = (params.get('utm_source') || '').trim();
            const utm_medium = (params.get('utm_medium') || '').trim();
            const utm_campaign = (params.get('utm_campaign') || '').trim();
            const utm_term = (params.get('utm_term') || '').trim();
            const utm_content = (params.get('utm_content') || '').trim();
            const fbclid = (params.get('fbclid') || '').trim();

            if (utm_source || utm_medium || utm_campaign || utm_term || utm_content || fbclid) {
                fromUrl = {
                    utm_source,
                    utm_medium,
                    utm_campaign,
                    utm_term,
                    utm_content,
                    fbclid,
                    expires_at: Date.now() + UTM_TTL_MS,
                };
                global.localStorage.setItem(UTM_KEY, JSON.stringify(fromUrl));
            }
        } catch (_e) {
            /* ignore */
        }

        if (fromUrl) {
            return {
                utm_source: fromUrl.utm_source,
                utm_medium: fromUrl.utm_medium,
                utm_campaign: fromUrl.utm_campaign,
                utm_term: fromUrl.utm_term,
                utm_content: fromUrl.utm_content,
                fbclid: fromUrl.fbclid,
            };
        }

        try {
            const raw = global.localStorage.getItem(UTM_KEY);
            if (!raw) return empty;
            const stored = JSON.parse(raw);
            if (!stored || typeof stored !== 'object') return empty;
            if (Number(stored.expires_at) && Date.now() > Number(stored.expires_at)) {
                global.localStorage.removeItem(UTM_KEY);
                return empty;
            }
            return {
                utm_source: String(stored.utm_source || ''),
                utm_medium: String(stored.utm_medium || ''),
                utm_campaign: String(stored.utm_campaign || ''),
                utm_term: String(stored.utm_term || ''),
                utm_content: String(stored.utm_content || ''),
                fbclid: String(stored.fbclid || ''),
            };
        } catch (_e) {
            return empty;
        }
    }

    /**
     * @param {string} pageTitle
     * @returns {boolean}
     */
    function shouldTrackPage(pageTitle) {
        if (!pageTitle || !TRACKED_PAGES[pageTitle]) return false;
        if (APP_ONLY_PAGES[pageTitle] && !isInApp()) return false;
        return true;
    }

    /**
     * Shared identity / context fields for page_view and button_click.
     * @param {string} page_title
     * @returns {object}
     */
    function buildCommonFields(page_title) {
        const platform = getPlatform();
        const utm = syncAndGetUtm();

        return {
            visitor_id: getVisitorId(),
            session_id: getSessionId(),
            customer_id: getCustomerId(),
            page_url: String(global.location?.href || ''),
            page_path: String(global.location?.pathname || ''),
            page_title,
            referrer: String(global.document?.referrer || ''),
            platform,
            device: getDevice(platform),
            locale: getLocale(),
            utm_source: utm.utm_source,
            utm_medium: utm.utm_medium,
            utm_campaign: utm.utm_campaign,
            utm_term: utm.utm_term,
            utm_content: utm.utm_content,
            fbclid: utm.fbclid,
            user_agent: String(global.navigator?.userAgent || ''),
            ts: Date.now(),
        };
    }

    /**
     * @param {string} href
     * @returns {string}
     */
    function resolveLink(href) {
        const raw = String(href || '').trim();
        if (!raw) return '';
        try {
            return new URL(raw, global.location?.href || undefined).href;
        } catch (_e) {
            return raw;
        }
    }

    /**
     * Current search keyword from URL `q` (search results page).
     * @returns {string}
     */
    function getSearchKeyword() {
        try {
            const params = new URLSearchParams(global.location.search || '');
            return String(params.get('q') || '').trim();
        } catch (_e) {
            return '';
        }
    }

    /**
     * On searchresult clicks, button_id is always the search keyword.
     * @param {string} [fallback]
     * @returns {string}
     */
    function resolveClickButtonId(fallback) {
        if (getPageTitle() === 'searchresult') return getSearchKeyword();
        return fallback == null ? '' : String(fallback);
    }

    /**
     * Build page_view payload (same shape as future API body).
     * @returns {object|null}
     */
    root.buildPageViewPayload = function buildPageViewPayload() {
        const page_title = getPageTitle();
        if (!shouldTrackPage(page_title)) return null;

        return {
            ...buildCommonFields(page_title),
            event: 'page_view',
            module_name: '',
            button_name: '',
            button_id: '',
            button_index: 0,
            extra: null,
        };
    };

    /**
     * @param {{
     *   module_name: string,
     *   button_name: string,
     *   button_id?: string,
     *   button_index?: number,
     *   extra?: object|null,
     * }} opts
     * @returns {object|null}
     */
    root.buildButtonClickPayload = function buildButtonClickPayload(opts) {
        const page_title = getPageTitle();
        if (!shouldTrackPage(page_title)) return null;
        if (!opts || !opts.module_name || !opts.button_name) return null;

        return {
            ...buildCommonFields(page_title),
            event: 'button_click',
            module_name: String(opts.module_name),
            button_name: String(opts.button_name),
            button_id: String(opts.button_id || ''),
            button_index: Number(opts.button_index) > 0 ? Number(opts.button_index) : 0,
            extra: opts.extra && typeof opts.extra === 'object' ? opts.extra : null,
        };
    };

    /**
     * Modal / overlay exposure (not counted as page_view).
     * @param {{
     *   module_name: string,
     *   button_name: string,
     *   button_id?: string,
     *   button_index?: number,
     *   extra?: object|null,
     * }} opts
     * @returns {object|null}
     */
    root.buildModalViewPayload = function buildModalViewPayload(opts) {
        const page_title = getPageTitle();
        if (!shouldTrackPage(page_title)) return null;
        if (!opts || !opts.module_name || !opts.button_name) return null;

        return {
            ...buildCommonFields(page_title),
            event: 'modal_view',
            module_name: String(opts.module_name),
            button_name: String(opts.button_name),
            button_id: String(opts.button_id || ''),
            button_index: Number(opts.button_index) > 0 ? Number(opts.button_index) : 0,
            extra: opts.extra && typeof opts.extra === 'object' ? opts.extra : null,
        };
    };

    /**
     * Behavior track endpoint: {apiBaseUrl}/api/v1/shopfiy/offhoo/track
     * @returns {string}
     */
    function getTrackApiUrl() {
        try {
            const offhoo = global.Theme && global.Theme.offhoo;
            if (offhoo && offhoo.trackApiUrl) return String(offhoo.trackApiUrl).trim();
            if (offhoo && typeof offhoo.joinApi === 'function') {
                return String(offhoo.joinApi(TRACK_PATH) || '').trim();
            }
            const base = String(offhoo?.apiBaseUrl || '').trim().replace(/\/+$/, '');
            if (!base) return '';
            return base + TRACK_PATH;
        } catch (_e) {
            return '';
        }
    }

    /**
     * @param {object} payload
     * @param {string} [apiUrl] if omitted, resolve via getTrackApiUrl()
     * @returns {Promise<boolean>}
     */
    function sendToBackend(payload, apiUrl) {
        const url = String(apiUrl || getTrackApiUrl() || '').trim();
        if (!url) {
            console.warn('[OffhooAnalytics] track API not configured (apiBaseUrl)');
            return Promise.resolve(false);
        }

        return fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
            body: JSON.stringify(payload),
            credentials: 'omit',
            mode: 'cors',
            keepalive: true,
        })
            .then((res) => {
                if (!res.ok) {
                    console.warn('[OffhooAnalytics] track failed', res.status, url);
                    return false;
                }
                return true;
            })
            .catch((err) => {
                console.warn('[OffhooAnalytics] track error', err);
                return false;
            });
    }

    /**
     * @param {{
     *   persisted?: boolean,
     *   tabType?: string,
     *   source?: string,
     *   module_name?: string,
     *   button_name?: string,
     *   button_id?: string,
     *   extra?: object|null,
     * }|PageTransitionEvent} [event]
     * @returns {boolean}
     */
    root.trackPageView = function trackPageView(event) {
        const payload = root.buildPageViewPayload();
        if (!payload) return false;

        if (event && typeof event === 'object') {
            if (event.module_name != null && String(event.module_name) !== '') {
                payload.module_name = String(event.module_name);
            }
            if (event.button_name != null && String(event.button_name) !== '') {
                payload.button_name = String(event.button_name);
            }
            if (event.button_id != null && String(event.button_id) !== '') {
                payload.button_id = String(event.button_id);
            }
            if (event.extra && typeof event.extra === 'object') {
                payload.extra = event.extra;
            }
        }

        const apiUrl = getTrackApiUrl();
        console.log('[OffhooAnalytics] page_view', {
            api_url: apiUrl || '(not configured)',
            ...payload,
            persisted: Boolean(event && event.persisted),
            source: (event && event.source) || 'boot',
            tabType: (event && event.tabType) || '',
            readyState: global.document ? global.document.readyState : '',
        });

        void sendToBackend(payload, apiUrl);
        return true;
    };

    /**
     * @param {{
     *   module_name: string,
     *   button_name: string,
     *   button_id?: string,
     *   button_index?: number,
     *   extra?: object|null,
     * }} opts
     * @returns {boolean}
     */
    root.trackButtonClick = function trackButtonClick(opts) {
        const payload = root.buildButtonClickPayload(opts);
        if (!payload) return false;

        const apiUrl = getTrackApiUrl();
        console.log('[OffhooAnalytics] button_click', {
            api_url: apiUrl || '(not configured)',
            ...payload,
        });

        void sendToBackend(payload, apiUrl);
        return true;
    };

    /**
     * @param {{
     *   module_name: string,
     *   button_name: string,
     *   button_id?: string,
     *   button_index?: number,
     *   extra?: object|null,
     * }} opts
     * @returns {boolean}
     */
    root.trackModalView = function trackModalView(opts) {
        const payload = root.buildModalViewPayload(opts);
        if (!payload) return false;

        const apiUrl = getTrackApiUrl();
        console.log('[OffhooAnalytics] modal_view', {
            api_url: apiUrl || '(not configured)',
            ...payload,
        });

        void sendToBackend(payload, apiUrl);
        return true;
    };

    /**
     * App-native track (bypasses page whitelist).
     * Supported event: page_view | button_click | app_open | modal_view
     *
     * @param {{
     *   event: string,
     *   device_id?: string,
     *   button_id?: string,
     *   button_name?: string,
     *   module_name?: string,
     *   extra?: object|null,
     * }} opts
     * @returns {boolean}
     */
    root.trackAppEvent = function trackAppEvent(opts) {
        if (!opts || typeof opts !== 'object') return false;

        const eventName = String(opts.event || '')
            .trim()
            .toLowerCase();

        let button_name = String(opts.button_name || '').trim();
        let button_id = String(opts.button_id || '').trim();
        let module_name = String(opts.module_name || '').trim();

        const deviceId = String(opts.device_id || '').trim();
        const page_title = getPageTitle();
        const extra =
            opts.extra && typeof opts.extra === 'object'
                ? { ...opts.extra }
                : {};
        if (deviceId) extra.device_id = deviceId;

        if (eventName === 'app_open') {
            button_id = deviceId;
        }

        // App language switch
        if (button_name === 'language') {
            module_name = 'app_language';
        }

        const payload = {
            ...buildCommonFields(page_title),
            event: eventName,
            module_name,
            button_name,
            button_id,
            button_index: Number(opts.button_index) > 0 ? Number(opts.button_index) : 0,
            extra: Object.keys(extra).length ? extra : null,
        };

        const apiUrl = getTrackApiUrl();
        console.log('[OffhooAnalytics] trackAppEvent', {
            api_url: apiUrl || '(not configured)',
            ...payload,
        });

        void sendToBackend(payload, apiUrl);
        return true;
    };

    /**
     * Homepage banner click: topbanner(1) / middlebanner(2).
     * @param {Element} el
     * @returns {boolean}
     */
    function trackBannerClickFromEl(el) {
        const scope = el.closest('[data-offhoo-track-module]');
        if (!scope) return false;

        const module_name = String(scope.getAttribute('data-offhoo-track-module') || '').trim();
        if (module_name !== 'topbanner' && module_name !== 'middlebanner') return false;

        const button_index = Number(scope.getAttribute('data-offhoo-track-index') || 0);
        const link = resolveLink(el.getAttribute('href'));
        const collection_id = String(
            el.getAttribute('data-offhoo-track-collection-id') ||
                scope.getAttribute('data-offhoo-track-collection-id') ||
                ''
        ).trim();
        const collection_title = String(
            el.getAttribute('data-offhoo-track-collection-title') ||
                scope.getAttribute('data-offhoo-track-collection-title') ||
                ''
        ).trim();
        const button_name =
            String(el.getAttribute('data-offhoo-track-button-name') || '').trim() ||
            collection_title ||
            'banner';

        const extra = {};
        if (link) extra.link = link;
        if (collection_id) extra.collection_id = collection_id;
        if (collection_title) extra.collection_title = collection_title;

        return root.trackButtonClick({
            module_name,
            button_name,
            button_id: collection_id,
            button_index,
            extra: Object.keys(extra).length ? extra : null,
        });
    }

    /**
     * @param {Element|null} scope
     * @returns {boolean}
     */
    function isShelfScope(scope) {
        if (!scope || !scope.classList) return false;
        return (
            scope.classList.contains('offhoo-product-shelf') ||
            scope.classList.contains('offhoo-collection-spotlight') ||
            scope.classList.contains('offhoo-product-feed') ||
            scope.classList.contains('offhoo-recently-viewed') ||
            scope.classList.contains('offhoo-user-center__products') ||
            scope.classList.contains('offhoo-main-collection') ||
            scope.classList.contains('offhoo-search-results')
        );
    }

    /**
     * @param {Element} scope
     * @returns {{ collection_id: string, collection_title: string }}
     */
    function getShelfCollectionMeta(scope) {
        return {
            collection_id: String(scope.getAttribute('data-offhoo-track-collection-id') || '').trim(),
            collection_title: String(scope.getAttribute('data-offhoo-track-collection-title') || '').trim(),
        };
    }

    /**
     * @param {Element} card
     * @param {Element} scope
     * @returns {object}
     */
    function buildProductExtra(card, scope) {
        const collection = getShelfCollectionMeta(scope);
        const link = resolveLink(card.getAttribute('data-offhoo-track-product-url'));
        const priceRaw = String(card.getAttribute('data-offhoo-track-product-price') || '').trim();
        const priceNum = Number(priceRaw);
        return {
            link,
            product_id: String(card.getAttribute('data-offhoo-track-product-id') || '').trim(),
            price: Number.isFinite(priceNum) ? priceNum : priceRaw,
            name: String(card.getAttribute('data-offhoo-track-product-name') || '').trim(),
            collection_id: collection.collection_id,
            collection_title: collection.collection_title,
        };
    }

    /**
     * Shelf «more» (view all).
     * @param {Element} el
     * @returns {boolean}
     */
    function trackShelfMoreClickFromEl(el) {
        const scope = el.closest('[data-offhoo-track-module]');
        if (!isShelfScope(scope)) return false;

        const module_name = String(scope.getAttribute('data-offhoo-track-module') || '').trim();
        if (!module_name) return false;

        const collection = getShelfCollectionMeta(scope);
        const button_index = Number(scope.getAttribute('data-offhoo-track-index') || 0);
        const link = resolveLink(el.getAttribute('href'));

        return root.trackButtonClick({
            module_name,
            button_name: 'more',
            button_id: collection.collection_id,
            button_index,
            extra: {
                link,
                collection_id: collection.collection_id,
                collection_title: collection.collection_title,
            },
        });
    }

    /**
     * Shelf / feed / user-center product / add_cart.
     * @param {Element} el
     * @param {'product'|'add_cart'} button_name
     * @returns {boolean}
     */
    function trackShelfProductClickFromEl(el, button_name) {
        const scope = el.closest('[data-offhoo-track-module]');
        if (!isShelfScope(scope)) return false;

        const module_name = String(scope.getAttribute('data-offhoo-track-module') || '').trim();
        if (!module_name) return false;

        const card = el.closest('[data-offhoo-track-product]');
        if (!card) return false;

        const product_id = String(card.getAttribute('data-offhoo-track-product-id') || '').trim();
        const button_index = Number(card.getAttribute('data-offhoo-track-product-index') || 0);

        let resolved_button_name = button_name;
        if (button_name === 'product') {
            const override = String(scope.getAttribute('data-offhoo-track-product-button-name') || '').trim();
            if (override) resolved_button_name = override;
        } else if (button_name === 'add_cart') {
            const override = String(scope.getAttribute('data-offhoo-track-add-cart-button-name') || '').trim();
            if (override) resolved_button_name = override;
        }

        return root.trackButtonClick({
            module_name,
            button_name: resolved_button_name,
            button_id: resolveClickButtonId(product_id),
            button_index,
            extra: buildProductExtra(card, scope),
        });
    }

    /**
     * Homepage scene grid card click.
     * @param {Element} el
     * @returns {boolean}
     */
    function trackSceneClickFromEl(el) {
        const scope = el.closest('[data-offhoo-track-module]');
        if (!scope || !scope.classList.contains('offhoo-scene-grid')) return false;

        const module_name = String(scope.getAttribute('data-offhoo-track-module') || '').trim() || 'scene';
        const button_index = Number(el.getAttribute('data-offhoo-track-index') || 0);
        const collection_id = String(el.getAttribute('data-offhoo-track-collection-id') || '').trim();
        const collection_title = String(el.getAttribute('data-offhoo-track-collection-title') || '').trim();
        const link = resolveLink(el.getAttribute('href'));

        return root.trackButtonClick({
            module_name,
            button_name: collection_title,
            button_id: collection_id,
            button_index,
            extra: {
                link,
                collection_id,
                collection_title,
            },
        });
    }

    /**
     * Header taxonomy menu / App category index card click.
     * @param {Element} el
     * @returns {boolean}
     */
    function trackMenuClickFromEl(el) {
        const menu_level = String(el.getAttribute('data-offhoo-track-menu-level') || '').trim();
        if (menu_level !== '1' && menu_level !== '2') return false;

        const button_name = String(el.getAttribute('data-offhoo-track-button-name') || '').trim();
        if (!button_name) return false;

        const button_id = String(el.getAttribute('data-offhoo-track-collection-id') || '').trim();
        const collection_title = String(el.getAttribute('data-offhoo-track-collection-title') || '').trim();
        const button_index = Number(el.getAttribute('data-offhoo-track-index'));
        const link = resolveLink(el.getAttribute('href'));
        const parent_id = String(el.getAttribute('data-offhoo-track-parent-id') || '').trim();
        const parent_name = String(el.getAttribute('data-offhoo-track-parent-name') || '').trim();
        const parent_index_raw = Number(el.getAttribute('data-offhoo-track-parent-index'));

        const extra = {
            link,
            collection_id: button_id,
            collection_title,
            menu_level,
            parent_id: '',
            parent_name: '',
        };

        if (menu_level === '2') {
            extra.parent_id = parent_id;
            extra.parent_name = parent_name;
            extra.parent_index = Number.isFinite(parent_index_raw) ? parent_index_raw : 0;
        }

        return root.trackButtonClick({
            module_name: 'menu',
            button_name,
            button_id,
            button_index: Number.isFinite(button_index) ? button_index : 0,
            extra,
        });
    }

    /**
     * Collect cart lines for pay click extra.
     * @param {Element} el
     * @returns {{ items: Array<object>, total_quantity: number, total_price: number }}
     */
    function collectCartPayExtra(el) {
        const scope =
            (el && el.closest('[data-offhoo-app-cart]')) ||
            (el && el.closest('form[action*="/cart"]')) ||
            global.document;

        const rows = scope.querySelectorAll('[data-offhoo-track-cart-line]');
        const items = [];
        let total_quantity = 0;
        let total_price = 0;

        rows.forEach((row) => {
            const variant_id = String(row.getAttribute('data-offhoo-track-variant-id') || '').trim();
            const product_id = String(row.getAttribute('data-offhoo-track-product-id') || '').trim();
            if (!variant_id && !product_id) return;

            let quantity = Number(row.getAttribute('data-offhoo-track-quantity') || 0);
            const qtyInput = row.querySelector(
                'input[name="updates[]"], input[name^="updates["], quantity-selector input, input[type="number"]'
            );
            if (qtyInput && qtyInput.value != null && String(qtyInput.value).trim() !== '') {
                const liveQty = Number(qtyInput.value);
                if (Number.isFinite(liveQty) && liveQty > 0) quantity = liveQty;
            }
            if (!Number.isFinite(quantity) || quantity <= 0) quantity = 1;

            const priceRaw = Number(row.getAttribute('data-offhoo-track-product-price') || 0);
            const unitPrice = Number.isFinite(priceRaw) ? priceRaw : 0;
            const linePriceRaw = Number(row.getAttribute('data-line-price') || 0);
            const lineTotal =
                Number.isFinite(linePriceRaw) && linePriceRaw > 0 ? linePriceRaw : unitPrice * quantity;

            items.push({
                variant_id,
                product_id,
                name: String(row.getAttribute('data-offhoo-track-product-name') || '').trim(),
                price: unitPrice,
                quantity,
            });

            total_quantity += quantity;
            total_price += lineTotal;
        });

        return { items, total_quantity, total_price };
    }

    /**
     * PDP add-to-cart / buy-now product snapshot from the product form.
     * @param {Element} el
     * @returns {object}
     */
    function collectPdpProductExtra(el) {
        const formRoot =
            (el && el.closest('product-form-component')) ||
            (el && el.closest('form[data-type="add-to-cart-form"]')) ||
            null;
        const form =
            (formRoot && formRoot.tagName === 'FORM' && formRoot) ||
            (formRoot && formRoot.querySelector && formRoot.querySelector('form')) ||
            (el && el.closest('form')) ||
            null;

        const product_id = String(
            (formRoot && formRoot.getAttribute('data-offhoo-track-product-id')) ||
                (formRoot && formRoot.getAttribute('data-product-id')) ||
                ''
        ).trim();
        const name = String(
            (formRoot && formRoot.getAttribute('data-offhoo-track-product-name')) || ''
        ).trim();
        const link = resolveLink(
            (formRoot && formRoot.getAttribute('data-offhoo-track-product-url')) ||
                (formRoot && formRoot.getAttribute('data-product-url')) ||
                ''
        );

        let variant_id = '';
        let quantity = 1;
        if (form) {
            const idInput = form.querySelector('input[name="id"], select[name="id"]');
            if (idInput && idInput.value != null) variant_id = String(idInput.value).trim();
            const qtyInput = form.querySelector('input[name="quantity"]');
            if (qtyInput && qtyInput.value != null && String(qtyInput.value).trim() !== '') {
                const q = Number(qtyInput.value);
                if (Number.isFinite(q) && q > 0) quantity = q;
            }
        }

        const priceRaw = Number(
            (formRoot && formRoot.getAttribute('data-offhoo-track-product-price')) || 0
        );

        return {
            link,
            product_id,
            variant_id,
            name,
            price: Number.isFinite(priceRaw) ? priceRaw : 0,
            quantity,
        };
    }

    /**
     * Product detail addtocart / buy.
     * @param {Element} el
     * @param {'addtocart'|'buy'} module_name
     * @returns {boolean}
     */
    function trackPdpCtaClickFromEl(el, module_name) {
        if (!el || !module_name) return false;
        if (getPageTitle() !== 'productdetail') return false;

        const button_name =
            String(el.getAttribute('data-offhoo-track-button-name') || '').trim() || module_name;
        const extra = collectPdpProductExtra(el);

        return root.trackButtonClick({
            module_name,
            button_name,
            button_id: extra.variant_id || extra.product_id || '',
            button_index: 0,
            extra,
        });
    }

    /**
     * App title-bar / PDP floating back.
     * @param {Element} el
     * @returns {boolean}
     */
    function trackAppBackClickFromEl(el) {
        if (!el) return false;

        const page = getPageTitle();
        if (page !== 'productlist' && page !== 'productdetail' && page !== 'searchresult') {
            return false;
        }

        const button_name =
            String(el.getAttribute('data-offhoo-track-button-name') || '').trim() || 'back';

        return root.trackButtonClick({
            module_name: 'back',
            button_name,
            button_id: resolveClickButtonId(''),
            button_index: 0,
            extra: null,
        });
    }

    /**
     * Header chrome / cart pay / me page actions.
     * @param {Element} el
     * @param {'search'|'top_user_icon'|'top_cart_icon'|'backtop'|'pay'|'login'|'settings'} module_name
     * @returns {boolean}
     */
    function trackHeaderChromeClickFromEl(el, module_name) {
        if (!el || !module_name) return false;

        const button_name =
            String(el.getAttribute('data-offhoo-track-button-name') || '').trim() || module_name;

        let extra = null;
        if (module_name === 'pay') {
            extra = collectCartPayExtra(el);
        } else if (module_name === 'settings') {
            let loggedInAttr = String(el.getAttribute('data-offhoo-track-logged-in') || '').trim();
            if (loggedInAttr === '') {
                const root = el.closest('[data-offhoo-user-center]');
                loggedInAttr = String((root && root.getAttribute('data-offhoo-track-logged-in')) || '').trim();
            }
            if (loggedInAttr === '') {
                try {
                    const cid = global.Theme && global.Theme.offhoo && global.Theme.offhoo.customerId;
                    loggedInAttr = cid ? '1' : '0';
                } catch (_e) {
                    loggedInAttr = '0';
                }
            }
            extra = { is_logged_in: loggedInAttr === '1' || loggedInAttr === 'true' };
        }

        return root.trackButtonClick({
            module_name,
            button_name,
            button_id: '',
            button_index: 0,
            extra,
        });
    }

    /**
     * @param {Element} input
     * @returns {string}
     */
    function getFilterInputLabel(input) {
        if (!input) return '';
        try {
            const labels = input.labels;
            const label = labels && labels.length ? labels[0] : null;
            if (label) {
                const span = label.querySelector('span');
                return String(((span && span.textContent) || label.textContent) || '')
                    .replace(/\s+/g, ' ')
                    .trim();
            }
        } catch (_e) {
            /* ignore */
        }
        return '';
    }

    /**
     * @returns {{ filter: string, sort: string }|null}
     */
    function getFilterSortButtonNames() {
        const page = getPageTitle();
        if (page === 'productlist') return { filter: 'filter', sort: 'sort' };
        if (page === 'searchresult') {
            return { filter: 'search_result_filter', sort: 'search_result_sort' };
        }
        return null;
    }

    /**
     * @param {Element} el
     * @returns {boolean}
     */
    function isProductListFilterScope(el) {
        return Boolean(el && el.closest && el.closest('.offhoo-main-collection'));
    }

    /**
     * @param {Element} el
     * @returns {boolean}
     */
    function isSearchFilterScope(el) {
        return Boolean(
            el &&
                el.closest &&
                (el.closest('.offhoo-search-results') ||
                    el.closest('facets-form-component[data-page-type="search"]') ||
                    el.closest('#filters-drawer'))
        );
    }

    /**
     * Resolve sort label/value from select or radio.
     * @param {HTMLSelectElement|HTMLInputElement} input
     * @returns {{ sort_by: string, sort_label: string }}
     */
    function resolveSortMeta(input) {
        const sort_by = String(input.value || '').trim();
        let sort_label = '';
        if (input.tagName === 'SELECT') {
            const opt = input.options && input.options[input.selectedIndex];
            sort_label = opt
                ? String(opt.textContent || '')
                      .replace(/\s+/g, ' ')
                      .trim()
                : '';
        } else {
            sort_label = String(
                input.getAttribute('data-option-name') || getFilterInputLabel(input) || ''
            )
                .replace(/\s+/g, ' ')
                .trim();
        }
        return { sort_by, sort_label };
    }

    /**
     * Product list / search sort change.
     * @param {HTMLSelectElement|HTMLInputElement} input
     * @returns {boolean}
     */
    function trackCollectionSortChange(input) {
        const names = getFilterSortButtonNames();
        if (!input || !names) return false;

        const page = getPageTitle();
        if (page === 'productlist' && !isProductListFilterScope(input)) return false;
        if (page === 'searchresult' && !isSearchFilterScope(input)) return false;

        const meta = resolveSortMeta(input);
        return root.trackButtonClick({
            module_name: 'sort',
            button_name: names.sort,
            button_id: resolveClickButtonId(meta.sort_by),
            button_index: 0,
            extra: { sort_by: meta.sort_by, sort_label: meta.sort_label },
        });
    }

    /**
     * Product list / search filter input change.
     * @param {HTMLInputElement} input
     * @returns {boolean}
     */
    function trackCollectionFilterChange(input) {
        const names = getFilterSortButtonNames();
        if (!input || !names) return false;

        const page = getPageTitle();
        if (page === 'productlist' && !isProductListFilterScope(input)) return false;
        if (page === 'searchresult' && !isSearchFilterScope(input)) return false;

        let filter_type = 'facet';
        if (input.matches('[data-offhoo-tag-filter]')) filter_type = 'tag';
        else if (input.matches('[data-offhoo-price-range]')) filter_type = 'price';
        else if (input.matches('.price-facet__input') || /filter\.v\.price/.test(String(input.name || ''))) {
            filter_type = 'price';
        }

        const label = getFilterInputLabel(input) || String(input.getAttribute('data-label') || '').trim();
        let value = String(input.value || '').trim();
        if (filter_type === 'tag') {
            value = String(input.getAttribute('data-tag') || value).trim();
        } else if (filter_type === 'price' && input.matches('[data-offhoo-price-range]')) {
            const min = String(input.getAttribute('data-price-min') || '').trim();
            const max = String(input.getAttribute('data-price-max') || '').trim();
            value = min + (max !== '' ? '-' + max : '+');
        }

        const checked =
            input.type === 'checkbox' || input.type === 'radio' ? Boolean(input.checked) : true;

        return root.trackButtonClick({
            module_name: 'filter',
            button_name: names.filter,
            button_id: resolveClickButtonId(value),
            button_index: 0,
            extra: {
                filter_type: filter_type,
                label: label,
                value: value,
                checked: checked,
                name: String(input.getAttribute('name') || '').trim(),
            },
        });
    }

    /**
     * Active filter chip remove / price clear / facets clear.
     * @param {Element} el
     * @returns {boolean}
     */
    function trackCollectionFilterClear(el) {
        const names = getFilterSortButtonNames();
        if (!el || !names) return false;

        const page = getPageTitle();
        if (page === 'productlist' && !isProductListFilterScope(el)) return false;
        if (page === 'searchresult' && !isSearchFilterScope(el)) return false;

        const isPriceClear = el.matches('[data-offhoo-price-clear]');
        const isClearAll = Boolean(el.closest && el.closest('.facets__clear-all, .facets__clear-all-link'));
        const label = String(el.textContent || '')
            .replace(/[×xX]\s*$/, '')
            .replace(/\s+/g, ' ')
            .trim();

        return root.trackButtonClick({
            module_name: 'filter',
            button_name: names.filter,
            button_id: resolveClickButtonId(''),
            button_index: 0,
            extra: {
                filter_type: isClearAll ? 'clear_all' : isPriceClear ? 'price_clear' : 'clear',
                label: label,
                checked: false,
            },
        });
    }

    /**
     * @param {Event} event
     */
    function onCollectionFilterSortChange(event) {
        const target = event.target;
        if (!target || typeof target.matches !== 'function') return;

        const page = getPageTitle();

        // Collection (Offhoo) sort / filter
        if (page === 'productlist') {
            if (target.matches('[data-offhoo-sort-form] select')) {
                trackCollectionSortChange(target);
                return;
            }
            if (
                target.matches(
                    '[data-offhoo-tag-filter], [data-offhoo-price-range], [data-offhoo-filter-auto-submit]'
                )
            ) {
                trackCollectionFilterChange(target);
            }
            return;
        }

        // Search results (Horizon facets)
        if (page !== 'searchresult') return;
        if (!isSearchFilterScope(target)) return;

        if (
            target.matches('select[name="sort_by"]') ||
            (target.matches('input[name="sort_by"]') && target.type === 'radio' && target.checked)
        ) {
            trackCollectionSortChange(target);
            return;
        }

        if (target.matches('input[type="checkbox"]') && String(target.name || '').indexOf('filter.') === 0) {
            trackCollectionFilterChange(target);
            return;
        }

        if (
            target.matches('.price-facet__input') ||
            (target.matches('input') && /filter\.v\.price/.test(String(target.name || '')))
        ) {
            trackCollectionFilterChange(target);
        }
    }

    /**
     * @param {MouseEvent} event
     */
    function onMarkedDocumentClick(event) {
        const target = event.target;
        if (!target || typeof target.closest !== 'function') return;

        const filterClear = target.closest(
            'a.offhoo-active-filter, [data-offhoo-price-clear], facet-remove-component, .facets__clear-all-link'
        );
        if (filterClear) {
            trackCollectionFilterClear(filterClear);
        }

        const el = target.closest('[data-offhoo-track-click]');
        if (!el) return;

        const action = String(el.getAttribute('data-offhoo-track-click') || '').trim();
        if (action === 'banner') {
            trackBannerClickFromEl(el);
            return;
        }
        if (action === 'scene') {
            trackSceneClickFromEl(el);
            return;
        }
        if (action === 'menu') {
            trackMenuClickFromEl(el);
            return;
        }
        if (
            action === 'search' ||
            action === 'top_user_icon' ||
            action === 'top_cart_icon' ||
            action === 'backtop' ||
            action === 'pay' ||
            action === 'login' ||
            action === 'settings'
        ) {
            trackHeaderChromeClickFromEl(el, action);
            return;
        }
        if (action === 'addtocart' || action === 'buy') {
            trackPdpCtaClickFromEl(el, action);
            return;
        }
        if (action === 'back') {
            trackAppBackClickFromEl(el);
            return;
        }
        if (action === 'more') {
            trackShelfMoreClickFromEl(el);
            return;
        }
        if (action === 'product') {
            trackShelfProductClickFromEl(el, 'product');
            return;
        }
        if (action === 'add_cart') {
            trackShelfProductClickFromEl(el, 'add_cart');
        }
    }

    /**
     * App Tab bar switched to this WebView → count as page exposure again.
     * module_name=app_nav_tab; button_name=app_home|app_category|app_cart|app_me
     * @param {CustomEvent} event
     */
    /**
     * Exact match: from_app_tab=1
     * @returns {boolean}
     */
    function isFromAppTabMarked() {
        try {
            const url = new URL(String(global.location && global.location.href ? global.location.href : ''));
            return url.searchParams.get(FROM_APP_TAB_PARAM) === '1';
        } catch (_e) {
            return false;
        }
    }

    /**
     * Mine / user-center login or account hooks — not a bottom-tab entry.
     * @returns {boolean}
     */
    function isMineAccountCallbackUrl() {
        try {
            const params = new URL(String(global.location.href)).searchParams;
            if (params.get('code') || params.get('error')) return true;
            if (params.get('clear_account') === '1') return true;
            if (params.get('logout_account') === '1') return true;
            return false;
        } catch (_e) {
            return false;
        }
    }

    /** Stamp URL so refresh / later loads keep from_app_tab=1 (not counted as tab again). */
    function markFromAppTabParam() {
        try {
            const url = new URL(String(global.location.href));
            if (url.searchParams.get(FROM_APP_TAB_PARAM) === '1') return;
            url.searchParams.set(FROM_APP_TAB_PARAM, '1');
            global.history.replaceState(global.history.state, '', url.toString());
        } catch (_e) {
            /* ignore */
        }
    }

    /**
     * App cold first open of category/cart/me often misses early onTabVisible (white screen).
     * No from_app_tab=1 → treat boot page_view as app_nav_tab, then stamp the URL.
     * @returns {{ source: string, module_name: string, button_name: string, extra: object }|null}
     */
    function getAppTabBootTrackOpts() {
        if (!isInApp()) return null;
        const page = getPageTitle();
        const button_name = PAGE_TO_APP_TAB_BUTTON[page];
        if (!button_name) return null;
        if (isFromAppTabMarked()) return null;
        if (page === 'me' && isMineAccountCallbackUrl()) return null;
        return {
            source: 'boot-app-tab',
            module_name: 'app_nav_tab',
            button_name,
            extra: { source: 'from_app_tab_missing', page },
        };
    }

    function onTabVisibleTrack(event) {
        const tabType = String((event && event.detail && event.detail.tabType) || '').trim();
        const expectedPage = TAB_TO_PAGE[tabType];
        if (!expectedPage) return;
        if (getPageTitle() !== expectedPage) return;

        // Same first paint already counted via missing from_app_tab
        if (appTabBootCounted) {
            appTabBootCounted = false;
            return;
        }

        const button_name = TAB_TO_BUTTON_NAME[tabType] || '';
        root.trackPageView({
            source: 'tab-visible',
            tabType,
            module_name: 'app_nav_tab',
            button_name,
            extra: { source: 'onTabVisible', tabType },
        });
    }

    function boot() {
        const tabOpts = getAppTabBootTrackOpts();
        if (tabOpts) {
            root.trackPageView(tabOpts);
            markFromAppTabParam();
            appTabBootCounted = true;
        } else {
            root.trackPageView({ source: 'boot' });
        }
        // App: switch back to this Tab
        global.addEventListener('offhoo:tab-visible', onTabVisibleTrack);
        // Click tracking (banner + shelf + scene + menu)
        if (global.document && global.document.addEventListener) {
            global.document.addEventListener('click', onMarkedDocumentClick, true);
            // Collection filter / sort (change, not click)
            global.document.addEventListener('change', onCollectionFilterSortChange, true);
        }
    }

    boot();
})(typeof window !== 'undefined' ? window : globalThis);
